import { useCallback, useEffect, useId, useRef, useState, type RefObject } from "react";
import {
  Check,
  MessageSquarePlus,
  MessageSquareText,
  Pencil,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { cn } from "@/utils/helpers";
import type { WorkshopArtifactNote } from "@/types/workshop.type";
import { formatAttribution } from "../attribution";
import {
  anchorFromSelection,
  locateAnchors,
  paintHighlight,
  rangeContainsPoint,
  type NoteAnchor,
} from "./artifactNoteAnchors";

/** `::highlight()` names; styled in workshop.css. */
const HIGHLIGHT_OPEN = "ws-note";
const HIGHLIGHT_ACTIVE = "ws-note-active";
const HIGHLIGHT_DRAFT = "ws-note-draft";

/** Where the "Add note" button floats, in the scroll container's content box. */
export type NoteSelectionPrompt = { anchor: NoteAnchor; top: number; left: number };

/**
 * The document side of artifact notes: turns a text selection inside `docRef`
 * into an "Add note" prompt, finds every note's passage in the rendered
 * document, and paints the passages without touching the DOM React owns.
 *
 * `enabled` is false while the document on screen is not the current version
 * (a past revision, or the editor), so nothing is anchored to stale text.
 */
export function useArtifactNoteLayer({
  docRef,
  scrollRef,
  notes,
  enabled,
  canAdd,
  draft,
  activeNoteId,
  onPickNote,
}: {
  docRef: RefObject<HTMLElement | null>;
  scrollRef: RefObject<HTMLElement | null>;
  notes: WorkshopArtifactNote[];
  enabled: boolean;
  canAdd: boolean;
  /** The passage a note is being written for; stays painted once the selection is gone. */
  draft: NoteAnchor | null;
  activeNoteId: string | null;
  onPickNote: (noteId: string) => void;
}) {
  const [prompt, setPrompt] = useState<NoteSelectionPrompt | null>(null);
  // Bumped whenever the rendered document changes (markdown renders in stages).
  const [domVersion, setDomVersion] = useState(0);
  const [ranges, setRanges] = useState<Map<string, Range | null>>(() => new Map());
  const openRanges = useRef<Array<[string, Range]>>([]);

  useEffect(() => {
    const doc = docRef.current;
    if (!doc || !enabled) return;
    let frame = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setDomVersion((v) => v + 1));
    });
    observer.observe(doc, { childList: true, subtree: true, characterData: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [docRef, enabled]);

  // Find every passage, then paint: open notes, the active one, the draft. On
  // the next frame, once the markdown this render produced is in the DOM.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const doc = docRef.current;
      if (!doc || !enabled) {
        openRanges.current = [];
        setRanges(new Map());
        paintHighlight(HIGHLIGHT_OPEN, []);
        paintHighlight(HIGHLIGHT_ACTIVE, []);
        paintHighlight(HIGHLIGHT_DRAFT, []);
        return;
      }
      const found = locateAnchors(doc, [...notes, ...(draft ? [draft] : [])]);
      const byId = new Map<string, Range | null>();
      notes.forEach((note, i) => byId.set(note.id, found[i]));
      setRanges(byId);
      openRanges.current = notes.flatMap((note, i) => {
        const range = found[i];
        return note.status === "open" && range ? [[note.id, range] as [string, Range]] : [];
      });
      paintHighlight(
        HIGHLIGHT_OPEN,
        openRanges.current.filter(([id]) => id !== activeNoteId).map(([, r]) => r),
      );
      const active = activeNoteId ? byId.get(activeNoteId) : null;
      paintHighlight(HIGHLIGHT_ACTIVE, active ? [active] : []);
      const draftRange = draft ? found[notes.length] : null;
      paintHighlight(HIGHLIGHT_DRAFT, draftRange ? [draftRange] : []);
    });
    return () => cancelAnimationFrame(frame);
  }, [docRef, enabled, notes, draft, activeNoteId, domVersion]);

  useEffect(
    () => () => {
      paintHighlight(HIGHLIGHT_OPEN, []);
      paintHighlight(HIGHLIGHT_ACTIVE, []);
      paintHighlight(HIGHLIGHT_DRAFT, []);
    },
    [],
  );

  // A selection inside the document offers "Add note"; clicking a passage opens its note.
  useEffect(() => {
    const doc = docRef.current;
    const scroller = scrollRef.current;
    if (!doc || !scroller || !enabled) return;

    const readSelection = () => {
      const selection = window.getSelection();
      const anchor = canAdd && selection ? anchorFromSelection(doc, selection) : null;
      if (!anchor || !selection) {
        setPrompt(null);
        return;
      }
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      const box = scroller.getBoundingClientRect();
      const half = 64;
      setPrompt({
        anchor,
        top: rect.top - box.top + scroller.scrollTop - 44,
        left: Math.min(
          Math.max(rect.left - box.left + rect.width / 2, half),
          Math.max(half, box.width - half),
        ),
      });
    };
    // Let the browser settle the selection before reading it.
    const onPointerUp = () => window.setTimeout(readSelection, 0);
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.shiftKey || event.key.startsWith("Arrow")) readSelection();
    };
    const onSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) setPrompt(null);
    };
    const onClick = (event: MouseEvent) => {
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) return;
      const hit = openRanges.current.find(([, range]) =>
        rangeContainsPoint(range, event.clientX, event.clientY),
      );
      if (hit) onPickNote(hit[0]);
    };

    doc.addEventListener("pointerup", onPointerUp);
    doc.addEventListener("keyup", onKeyUp);
    doc.addEventListener("click", onClick);
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      doc.removeEventListener("pointerup", onPointerUp);
      doc.removeEventListener("keyup", onKeyUp);
      doc.removeEventListener("click", onClick);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [docRef, scrollRef, enabled, canAdd, onPickNote]);

  const clearPrompt = useCallback(() => setPrompt(null), []);

  /** Scrolls the document to a note's passage, when it is still there. */
  const revealNote = useCallback(
    (noteId: string) => {
      const range = ranges.get(noteId);
      const target = range?.startContainer.parentElement;
      target?.scrollIntoView({ block: "center", behavior: "smooth" });
    },
    [ranges],
  );

  // A prompt left over from before the document went stale is never shown.
  return { prompt: enabled ? prompt : null, clearPrompt, ranges, revealNote };
}

/** The floating button over a fresh selection. */
export function AddNoteButton({
  prompt,
  onAdd,
}: {
  prompt: NoteSelectionPrompt;
  onAdd: (anchor: NoteAnchor) => void;
}) {
  return (
    <button
      type="button"
      className="ws-note-add"
      style={{ top: Math.max(prompt.top, 0), left: prompt.left }}
      // Keep the selection alive: a mousedown here would collapse it first.
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onAdd(prompt.anchor)}
    >
      <MessageSquarePlus className="size-3.5" aria-hidden />
      Add note
    </button>
  );
}

function formatAgo(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function Quote({ text, onClick }: { text: string; onClick?: () => void }) {
  const body = <span className="line-clamp-3">{text}</span>;
  return onClick ? (
    <button type="button" className="ws-note-quote text-start" onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className="ws-note-quote">{body}</div>
  );
}

function NoteCard({
  note,
  active,
  detached,
  viewerId,
  canWrite,
  canDelete,
  onReveal,
  onSave,
  onResolve,
  onReopen,
  onDelete,
}: {
  note: WorkshopArtifactNote;
  active: boolean;
  /** Its passage is not in the version on screen. */
  detached: boolean;
  viewerId: string | null;
  canWrite: boolean;
  canDelete: boolean;
  onReveal: () => void;
  onSave: (body: string) => void;
  onResolve: () => void;
  onReopen: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editId = useId();
  const isMine = !!viewerId && note.createdByUserId === viewerId;
  const pending = note.id.startsWith("temp-");
  const author = formatAttribution(note.attribution?.createdBy, viewerId, {
    includeContext: false,
  });
  const resolvedBy = formatAttribution(note.attribution?.resolvedBy, viewerId, {
    includeContext: false,
  });

  useEffect(() => {
    if (!confirmDelete) return;
    const timer = window.setTimeout(() => setConfirmDelete(false), 3000);
    return () => window.clearTimeout(timer);
  }, [confirmDelete]);

  const save = () => {
    const body = draft.trim();
    if (!body) return;
    setEditing(false);
    if (body !== note.body) onSave(body);
  };

  return (
    <li
      data-note-id={note.id}
      className={cn("ws-note-card", active && "ws-note-card--active", pending && "opacity-70")}
    >
      <Quote text={note.quote} onClick={detached ? undefined : onReveal} />
      {detached ? (
        <span className="tt-label text-co-faint">Passage not in this version</span>
      ) : null}

      {editing ? (
        <div className="flex flex-col gap-2">
          <label htmlFor={editId} className="sr-only">
            Edit note
          </label>
          <textarea
            id={editId}
            autoFocus
            rows={3}
            value={draft}
            maxLength={4000}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) save();
              if (event.key === "Escape") {
                event.stopPropagation();
                setEditing(false);
                setDraft(note.body);
              }
            }}
            className="ws-note-textarea"
          />
          <div className="flex justify-end gap-2">
            <KitButton
              variant="ghost"
              className="h-8 px-3 text-xs"
              onClick={() => {
                setEditing(false);
                setDraft(note.body);
              }}
            >
              Cancel
            </KitButton>
            <KitButton className="h-8 px-3 text-xs" isDisabled={!draft.trim()} onClick={save}>
              Save
            </KitButton>
          </div>
        </div>
      ) : (
        <p className="tt-read-s whitespace-pre-wrap text-co-text">{note.body}</p>
      )}

      <div className="flex items-center justify-between gap-2">
        <span className="tt-label truncate text-co-faint">
          {author ? `${author.replace(/^by /, "")} · ` : ""}
          {formatAgo(note.createdAt)}
        </span>
        {canWrite && !pending && !editing ? (
          <span className="flex shrink-0 items-center gap-0.5">
            {note.status === "open" ? (
              <button
                type="button"
                className="ws-note-action"
                title="Resolve"
                aria-label="Resolve note"
                onClick={onResolve}
              >
                <Check className="size-3.5" aria-hidden />
              </button>
            ) : (
              <button
                type="button"
                className="ws-note-action"
                title="Reopen"
                aria-label="Reopen note"
                onClick={onReopen}
              >
                <RotateCcw className="size-3.5" aria-hidden />
              </button>
            )}
            {isMine ? (
              <button
                type="button"
                className="ws-note-action"
                title="Edit"
                aria-label="Edit note"
                onClick={() => {
                  setDraft(note.body);
                  setEditing(true);
                }}
              >
                <Pencil className="size-3.5" aria-hidden />
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                className={cn("ws-note-action", confirmDelete && "ws-note-action--danger")}
                title={confirmDelete ? "Click again to delete" : "Delete"}
                aria-label={confirmDelete ? "Confirm delete note" : "Delete note"}
                onClick={() => {
                  if (confirmDelete) onDelete();
                  else setConfirmDelete(true);
                }}
              >
                <Trash2 className="size-3.5" aria-hidden />
                {confirmDelete ? <span className="text-[11px]">Delete?</span> : null}
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      {note.status === "resolved" ? (
        <div className="ws-note-resolution">
          <span className="tt-label text-co-muted">
            Resolved{resolvedBy ? ` ${resolvedBy}` : ""}
            {note.resolvedAt ? ` · ${formatAgo(note.resolvedAt)}` : ""}
          </span>
          {note.resolution ? (
            <p className="tt-read-s text-co-text2">{note.resolution}</p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** The note being written for a freshly selected passage. */
function NoteComposer({
  quote,
  onCancel,
  onSubmit,
}: {
  quote: string;
  onCancel: () => void;
  onSubmit: (body: string) => void;
}) {
  const [body, setBody] = useState("");
  const inputId = useId();
  const submit = () => {
    const text = body.trim();
    if (text) onSubmit(text);
  };
  return (
    <form
      className="ws-note-card ws-note-card--active"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Quote text={quote} />
      <label htmlFor={inputId} className="sr-only">
        Your note
      </label>
      <textarea
        id={inputId}
        autoFocus
        rows={3}
        value={body}
        maxLength={4000}
        placeholder="What should change here?"
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) submit();
          if (event.key === "Escape") {
            event.stopPropagation();
            onCancel();
          }
        }}
        className="ws-note-textarea"
      />
      <div className="flex justify-end gap-2">
        <KitButton variant="ghost" className="h-8 px-3 text-xs" onClick={onCancel}>
          Cancel
        </KitButton>
        <KitButton type="submit" className="h-8 px-3 text-xs" isDisabled={!body.trim()}>
          Add note
        </KitButton>
      </div>
    </form>
  );
}

/** Notes cards while the list loads. */
function NotesSkeleton() {
  return (
    <ul className="flex flex-col gap-2" aria-hidden>
      {[0, 1].map((i) => (
        <li key={i} className="ws-note-card animate-pulse">
          <span className="h-10 w-full bg-co-line" />
          <span className="h-3 w-3/4 bg-co-line" />
          <span className="h-3 w-1/3 bg-co-line" />
        </li>
      ))}
    </ul>
  );
}

/**
 * The notes over the document (stage 3: a pegged popover under the notes
 * button, a sheet on a phone): a composer for the passage being noted, then
 * the open notes and, behind a toggle, the resolved ones. Notes stay anchored
 * to their passages as before.
 */
export function ArtifactNotesDrawer({
  variant = "popover",
  notes,
  loading,
  ranges,
  locatable,
  activeNoteId,
  viewerId,
  canWrite,
  isProjectOwner,
  draft,
  onCancelDraft,
  onSubmitDraft,
  onReveal,
  onSave,
  onResolve,
  onReopen,
  onDelete,
  onClose,
}: {
  /** Under the notes button on the desk and a tablet; a sheet on a phone. */
  variant?: "popover" | "sheet";
  notes: WorkshopArtifactNote[];
  loading: boolean;
  ranges: Map<string, Range | null>;
  /** Passages can be found: the current version is on screen. */
  locatable: boolean;
  activeNoteId: string | null;
  viewerId: string | null;
  canWrite: boolean;
  isProjectOwner: boolean;
  draft: NoteAnchor | null;
  onCancelDraft: () => void;
  onSubmitDraft: (body: string) => void;
  onReveal: (noteId: string) => void;
  onSave: (noteId: string, body: string) => void;
  onResolve: (noteId: string) => void;
  onReopen: (noteId: string) => void;
  onDelete: (noteId: string) => void;
  onClose: () => void;
}) {
  const [showResolved, setShowResolved] = useState(false);
  const headingId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const open = notes.filter((n) => n.status === "open");
  const resolved = notes.filter((n) => n.status !== "open");

  // Bring the active note's card into view (a passage was clicked).
  useEffect(() => {
    // Only open notes' passages are clickable, and they are always listed.
    if (!activeNoteId) return;
    const frame = requestAnimationFrame(() =>
      listRef.current
        ?.querySelector(`[data-note-id="${activeNoteId}"]`)
        ?.scrollIntoView({ block: "nearest", behavior: "smooth" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [activeNoteId]);

  const card = (note: WorkshopArtifactNote) => (
    <NoteCard
      key={note.id}
      note={note}
      active={note.id === activeNoteId}
      detached={locatable && !ranges.get(note.id)}
      viewerId={viewerId}
      canWrite={canWrite}
      canDelete={canWrite && (note.createdByUserId === viewerId || isProjectOwner)}
      onReveal={() => onReveal(note.id)}
      onSave={(text) => onSave(note.id, text)}
      onResolve={() => onResolve(note.id)}
      onReopen={() => onReopen(note.id)}
      onDelete={() => onDelete(note.id)}
    />
  );

  return (
    <aside
      className={cn("ws-notes-drawer co-dropdown-panel wb-glass-pop", variant === "sheet" && "ws-notes-drawer--sheet")}
      aria-labelledby={headingId}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }}
    >
      {variant === "sheet" ? <span className="ws-sheet-grabber" aria-hidden="true" /> : null}
      <div className="ws-notes-head">
        <h3 id={headingId} className="ws-notes-title">
          Notes
        </h3>
        <span className="ws-notes-count">{open.length} open</span>
        <button
          type="button"
          className="ws-head-icon"
          aria-label="Close notes"
          title="Close notes"
          onClick={onClose}
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>

      <div ref={listRef} className="ws-notes-list custom-scrollbar">
        {draft ? (
          <NoteComposer
            // A new passage starts a fresh note.
            key={`${draft.prefix}|${draft.quote}|${draft.suffix}`}
            quote={draft.quote}
            onCancel={onCancelDraft}
            onSubmit={onSubmitDraft}
          />
        ) : null}

        {loading ? (
          <NotesSkeleton />
        ) : open.length === 0 && !draft ? (
          <div className="ws-notes-empty">
            <MessageSquareText className="ws-notes-empty-mark" strokeWidth={1.6} aria-hidden />
            <p className="ws-notes-empty-line">
              {canWrite
                ? "No open notes. Select a passage in the output to leave one; Workshop sees open notes and can work through them."
                : "No open notes."}
            </p>
            <p className="ws-notes-empty-sub">
              Resolved notes stay in the history of the version they were made on.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">{open.map(card)}</ul>
        )}

        {resolved.length > 0 ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="tt-label self-start px-1 text-co-muted hover:text-co-accent-ink"
              aria-expanded={showResolved}
              onClick={() => setShowResolved((v) => !v)}
            >
              {showResolved ? "Hide" : "Show"} {resolved.length} resolved
            </button>
            {showResolved ? <ul className="flex flex-col gap-2">{resolved.map(card)}</ul> : null}
          </div>
        ) : null}
      </div>
    </aside>
  );
}
